import { CheckIcon, XIcon } from "@primer/octicons-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { rp } from "../api/client";
import { useAction, useDecision, useWhoami } from "../api/hooks";
import { canIn, type IntentView } from "../api/types";
import { ErrorMessage, Loading } from "./common";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardHeader } from "./ui/card";
import { Input } from "./ui/input";
import { List, ListItem } from "./ui/list";
import { cn } from "../lib/utils";

type StepState = "done" | "current" | "todo" | "failed";
type Step = { label: string; state: StepState };

/** The change that brings the intent's spec to a person: in review, editing docs with checks. */
export const specChangeOf = (v: IntentView) => v.changes.find((c) => c.state === "review" && (c.docs?.length ?? 0) > 0);

/** Where an intent is, as four steps, and who acts next: the same states bee tracks, said plainly. */
export function workflow(v: IntentView): { steps: Step[]; next: string; who: "person" | "agent" | null } {
  const specChange = specChangeOf(v);
  const stable = v.spec?.class === "stable";
  const reviews = v.changes.filter((c) => c.state === "review" && !(c.docs?.length ?? 0));
  const proven = v.outcomes.filter((o) => o.state === "holds").length;
  const spec: StepState = stable ? "done" : "current";
  const build: StepState = v.state === "proven" ? "done" : v.state === "regressed" ? "failed" : stable ? "current" : "todo";
  const steps: Step[] = [
    { label: "Intent", state: "done" },
    { label: "Spec", state: spec },
    { label: "Build", state: build },
    { label: "Done", state: v.state === "proven" ? "done" : "todo" },
  ];
  if (v.state === "proven") return { steps, next: "Done", who: null };
  if (!stable) {
    if (specChange || v.spec) return { steps, next: "Approve the spec", who: "person" };
    return { steps, next: "Agent writes the spec", who: "agent" };
  }
  if (reviews.length) return { steps, next: `Review ${reviews.length} change${reviews.length === 1 ? "" : "s"}`, who: "person" };
  if (v.state === "regressed") return { steps, next: "Broken · agent fixes", who: "agent" };
  return { steps, next: `Agent builds · ${proven}/${v.outcomes.length} pass`, who: "agent" };
}

export function Steps({ v }: { v: IntentView }) {
  const { steps, next, who } = workflow(v);
  return (
    <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-md border px-4 py-3">
      <ol className="m-0 flex list-none flex-wrap items-center gap-2 p-0" aria-label="Steps">
        {steps.map((s, i) => (
          <li key={s.label} className="flex items-center gap-2">
            {i > 0 && <span className={cn("h-px w-6", s.state === "todo" ? "bg-border" : "bg-success")} aria-hidden="true" />}
            <span
              className={cn(
                "flex items-center gap-1.5 text-sm",
                s.state === "done" && "text-success",
                s.state === "current" && "font-semibold text-foreground",
                s.state === "failed" && "font-semibold text-danger",
                s.state === "todo" && "text-muted-foreground",
              )}
              aria-current={s.state === "current" || s.state === "failed" ? "step" : undefined}
            >
              <span
                className={cn(
                  "grid size-5 place-items-center rounded-full border text-xs",
                  s.state === "done" && "border-success bg-success text-on-emphasis",
                  s.state === "current" && "border-warning bg-warning-subtle",
                  s.state === "failed" && "border-danger bg-danger-subtle",
                )}
                aria-hidden="true"
              >
                {s.state === "done" ? <CheckIcon size={12} /> : s.state === "failed" ? <XIcon size={12} /> : i + 1}
              </span>
              {s.label}
            </span>
          </li>
        ))}
      </ol>
      <span className="ml-auto flex items-center gap-2 text-sm">
        {who === "person" && <Badge tone="yellow">Needs you</Badge>}
        {who === "agent" && <Badge>Agent</Badge>}
        {who && <span className="text-muted-foreground">{next}</span>}
      </span>
    </div>
  );
}

/**
 * The spec waiting for a person, as what will be built and how it will be proven, with approve
 * and reject here: the main human decision of an intent.
 */
export function SpecReview({ repo, v }: { repo: string; v: IntentView }) {
  const c = specChangeOf(v);
  return c ? <SpecToApprove repo={repo} c={c} /> : null;
}

function SpecToApprove({ repo, c }: { repo: string; c: IntentView["changes"][number] }) {
  const d = useDecision(repo, c.id, c.state);
  const me = useWhoami();
  const act = useAction(repo);
  const [why, setWhy] = useState<string | null>(null);
  const person = !!me.data && (!!me.data.user || me.data.kind === "admin");
  const canReview = person && me.data?.sub !== c.agent && canIn(me.data, "write", repo);
  const r = d.data?.outcomes;
  const jobs = [...new Set((r?.outcomes ?? []).flatMap((o) => o.proofs.map((p) => p.proof.job)))];
  return (
    <Card className="mt-0 border-warning-muted">
      <CardHeader className="flex-wrap bg-warning-subtle">
        <span className="mr-auto">To approve</span>
        <Link to={`${rp(repo)}/changes/${c.id}?tab=files`} className="text-xs font-normal">
          {c.docs?.[0]}
        </Link>
      </CardHeader>
      {d.isLoading ? (
        <Loading rows={3} />
      ) : (
        <List>
          {(r?.outcomes ?? []).map((o, i) => (
            <ListItem key={o.check} className="items-start hover:bg-transparent">
              <span className="w-5 shrink-0 text-muted-foreground">{i + 1}.</span>
              <span className="min-w-48 flex-1">{o.title || o.check}</span>
              <span className="flex flex-wrap items-center justify-end gap-1.5 text-xs">
                {o.proofs.map((p, j) => (
                  <span key={j} className="flex items-center gap-1.5">
                    {p.proof.kind && <Badge tone="blue">{p.proof.kind}</Badge>}
                    <span className="font-mono text-muted-foreground">{p.proof.test}</span>
                  </span>
                ))}
              </span>
            </ListItem>
          ))}
        </List>
      )}
      <div className="flex flex-wrap items-center gap-2 border-t border-border-muted px-4 py-3">
        <span className="mr-auto text-sm text-muted-foreground">{jobs.length > 0 && <>Runs in {jobs.join(", ")}</>}</span>
        {!canReview ? (
          <span className="text-sm text-muted-foreground">{me.data?.sub === c.agent ? "Another person approves" : "A person approves"}</span>
        ) : why === null ? (
          <>
            <Button size="sm" variant="danger" disabled={act.isPending} onClick={() => setWhy("")}>
              <XIcon /> Reject
            </Button>
            <Button size="sm" variant="primary" disabled={act.isPending} onClick={() => act.mutate({ path: `/v1/repos/${repo}/changes/${c.id}/approve`, body: {} })}>
              <CheckIcon /> Approve spec
            </Button>
          </>
        ) : (
          <form
            className="flex w-full flex-wrap items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              act.mutate({ path: `/v1/repos/${repo}/changes/${c.id}/reject`, body: { reason: why } }, { onSuccess: () => setWhy(null) });
            }}
          >
            <Input autoFocus className="min-w-48 flex-1" value={why} onChange={(e) => setWhy(e.target.value)} placeholder="Why? (the agent reads this)" />
            <Button size="sm" variant="danger" disabled={act.isPending || !why.trim()}>
              <XIcon /> Reject
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => setWhy(null)}>
              Cancel
            </Button>
          </form>
        )}
      </div>
      {act.error ? <ErrorMessage error={act.error} /> : null}
    </Card>
  );
}
